"use client"

// services/Recruit/pages/offerTemplates/index.jsx
import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Text,
    Flex,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Badge,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    Input,
    FormLabel,
    Select,
    Textarea,
    Switch,
    VStack,
    HStack
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon, CopyIcon, ViewIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { toast } from "react-toastify";
import CommonCheckTable from "common/components/checktable";
import CommonDeleteModel from "common/components/commonDeleteModel";
import CustomStatus from "common/components/status/CustomStatus";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";
import { globalStyles } from "common/theme/styles";
import DateFormatter from "common/utils/dateFormater";
import OfferContentEditor from "services/Recruit/components/OfferLetter/OfferContentEditor";
import editIcon from "common/assets/img/editIcon.png";
import deleteIcon from "common/assets/img/deleteIcon.png";
import offerLetterService from "services/Recruit/services/offerLetter";
import DOMPurify from 'dompurify';
import { defaultTemplateContent } from "services/Recruit/data/panel.Data";
import { templateCategoryOptions } from "services/Recruit/data/panel.Data";
import {
    getTableFilters,
    saveTableFilters,
    clearTableFilters,
    getInitialFilterState
} from "common/utils/filterUtils";

const OfferTemplates = () => {
    const title = "Offer Letter Templates";
    const TABLE_NAME = 'offerTemplates'; // Define table name constant
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]);
    const [deleteMany, setDeleteMany] = useState(false);
    const [displaySearchData, setDisplaySearchData] = useState(false);
    const [searchedData, setSearchedData] = useState([]);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [previewData, setPreviewData] = useState(null);

    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [sortColumn, setSortColumn] = useState(null)
    const [sortOrder, setSortOrder] = useState("")
    const [searchbox, setSearchbox] = useState(() => {
        const filters = getInitialFilterState(TABLE_NAME);
        return filters?.searchbox || "";
    });
    const [finalSearchedData, setFinalSearchedData] = useState([])
    const [metadata, setMetadata] = useState({})

    const colors = globalStyles.colors
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure()

    const [permission] = HasAccess([accessModuleTitles.OfferTemplates]);

    // Save filters whenever they change
    useEffect(() => {
        const filters = {
            searchbox: searchbox || "",
        };
        saveTableFilters(TABLE_NAME, filters);
    }, [searchbox]);

    // Use role access table permissions (accessUtils handles employee view restriction)
    const effectivePermission = permission || {};

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'standard',
        subject: '',
        content: '',
        isActive: true,
        isDefault: false
    });

    useEffect(() => {
        fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Refetch data when pagination changes
    useEffect(() => {
        fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize])
    const fetchData = async (
        page = currentPage,
        limit = pageSize,
        sortKey = sortColumn,
        order = sortOrder,
        searchTerm = "",
    ) => {
        setIsLoading(true)
        try {
            const params = { page, limit }
            if (sortKey) {
                params.sort = order === "desc" ? `-${sortKey}` : sortKey
            }
            if (searchTerm) {
                params.search = searchTerm
            }

            const response = await offerLetterService.getTemplates(params)
            // Service returns response.data, so response = { metadata, data }
            const list = response?.data ?? []
            const meta = response?.metadata ?? {
                page,
                limit,
                totalRecords: Array.isArray(list) ? list.length : 0,
                totalPages: Math.ceil((Array.isArray(list) ? list.length : 0) / limit),
            }

            const baseIndex = (meta.page - 1) * meta.limit
            const formattedData = (list || []).map((item, index) => ({
                _id: item._id,
                id: item._id,
                srNo: baseIndex + index + 1, // numbered across pages
                name: item.name,
                description: item.description || "-",
                category: item.category,
                isActive: item.isActive,
                isDefault: item.isDefault,
                usageCount: item.usageCount,
                lastUsed: item.lastUsed ? DateFormatter({ date: item.lastUsed }) : "Never",
                createdBy: `${item.createBy?.firstName || ""} ${item.createBy?.lastName || ""}`.trim() || "-",
                createdAt: DateFormatter({ date: item.createdAt }),
                rawData: item,
            }))

            setData(formattedData)
            setMetadata(meta)
        } catch (error) {
            toast.error("Failed to fetch templates")
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleOpenModal = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            setFormData({
                name: template.name,
                description: template.description || '',
                category: template.category,
                subject: template.subject,
                content: template.content,
                isActive: template.isActive,
                isDefault: template.isDefault
            });
        } else {
            setEditingTemplate(null);
            setFormData({
                name: '',
                description: '',
                category: 'standard',
                subject: '',
                content: defaultTemplateContent,
                isActive: true,
                isDefault: false
            });
        }
        onOpen();
    };

    const handleCloseModal = () => {
        setEditingTemplate(null);
        setFormData({
            name: '',
            description: '',
            category: 'standard',
            subject: '',
            content: '',
            isActive: true,
            isDefault: false
        });
        onClose();
    };
    const handleSearchChange = (term) => {
        const trimmed = term?.trim() ?? ""
        const pageToFetch = trimmed === "" ? currentPage : 1
        if (trimmed !== "" && currentPage !== 1) {
            setCurrentPage(1)
        }
        setSearchbox(trimmed)
        fetchData(pageToFetch, pageSize, sortColumn, sortOrder, trimmed)
    }

    const handleSortChange = (accessorKey) => {
        let newSortOrder = "asc"
        if (sortColumn === accessorKey) {
            newSortOrder = sortOrder === "asc" ? "desc" : "asc"
        }
        setSortColumn(accessorKey)
        setSortOrder(newSortOrder)
        fetchData(currentPage, pageSize, accessorKey, newSortOrder, searchbox)
    }

    const handleSubmit = async () => {
        if (!formData.name || !formData.subject || !formData.content) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            if (editingTemplate) {
                await offerLetterService.updateTemplate(editingTemplate._id, formData)
                toast.success("Template updated successfully")
                handleCloseModal()
                fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
            } else {
                await offerLetterService.createTemplate(formData)
                toast.success("Template created successfully")
                handleCloseModal()
                // Reset to page 1 when creating new template to see the newly added entry
                setCurrentPage(1)
                fetchData(1, pageSize, sortColumn, sortOrder, searchbox)
            }
        } catch (error) {
            toast.error(editingTemplate ? "Failed to update template" : "Failed to create template")
            console.error(error)
        }
    }

    const handleDuplicate = async (id) => {
        try {
            await offerLetterService.duplicateTemplate(id)
            toast.success("Template duplicated successfully")
            fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        } catch (error) {
            toast.error("Failed to duplicate template")
            console.error(error)
        }
    }


    const handleToggleActive = async (template) => {
        try {
            await offerLetterService.updateTemplate(template._id, { isActive: !template.isActive })
            toast.success(`Template ${template.isActive ? "deactivated" : "activated"} successfully`)
            fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
        } catch (error) {
            toast.error("Failed to update template")
            console.error(error)
        }
    }

    const handleDeleteTemplates = async (ids) => {
        try {
            await offerLetterService.deleteManyTemplates(ids)
            toast.success("Templates deleted successfully")
            setSelectedValues([])
            setDeleteMany(false)
            // If deleting all items on current page (except page 1), go back one page
            const remainingItems = data.length - ids.length
            if (remainingItems === 0 && currentPage > 1) {
                const newPage = currentPage - 1
                setCurrentPage(newPage)
                fetchData(newPage, pageSize, sortColumn, sortOrder, searchbox)
            } else {
                fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox)
            }
        } catch (error) {
            toast.error("Failed to delete templates")
            console.error(error)
        }
    }

    const actionHeader = {
        Header: "Actions",
        isSortable: false,
        center: true,
        cell: ({ row }) => {
            const template = row.original.rawData;

            return (
                <Flex justify="center" align="center" w="100%">
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            size="sm"
                            variant="ghost"
                            fontWeight="bold"
                            fontSize="xl"
                            color="gray.600"
                            _hover={{ bg: "gray.100" }}
                        >
                            ⋮
                        </MenuButton>
                        <MenuList minW="150px" shadow="md">
                            <MenuItem icon={<ViewIcon />} onClick={() => handlePreview(template._id)}>
                                Preview
                            </MenuItem>
                            {effectivePermission?.update && (
                                <>
                                    <MenuItem icon={<img src={editIcon} alt="edit" style={{ width: '16px' }} />} onClick={() => handleOpenModal(template)}>
                                        Edit
                                    </MenuItem>
                                    <MenuItem icon={<CopyIcon />} onClick={() => handleDuplicate(template._id)}>
                                        Duplicate
                                    </MenuItem>
                                    <MenuItem icon={template.isActive ? <CloseIcon /> : <CheckIcon />} onClick={() => handleToggleActive(template)}>
                                        {template.isActive ? 'Deactivate' : 'Activate'}
                                    </MenuItem>
                                </>
                            )}
                            {effectivePermission?.delete && (
                                <MenuItem
                                    color="red.500"
                                    icon={<img src={deleteIcon} alt="delete" style={{ width: '16px' }} />}
                                    onClick={() => {
                                        setDeleteMany(true);
                                        setSelectedValues([row.original.id]);
                                    }}
                                >
                                    Delete
                                </MenuItem>
                            )}
                        </MenuList>
                    </Menu>
                </Flex>
            );
        }
    };

    const tableColumns = [
        {
            Header: "#",
            accessor: "_id",
            isSortable: false,
            width: 50
        },
        {
            Header: "Name",
            accessor: "name",
            cell: ({ row }) => (
                <Flex align="center" gap={2}>
                    <Text>{row.original.name}</Text>
                    {row.original.isDefault && (
                        <Badge colorScheme="green" fontSize="xs">Default</Badge>
                    )}
                </Flex>
            )
        },
        {
            Header: "Category",
            accessor: "category",
            cell: ({ value }) => (
                <Badge colorScheme="purple">
                    {templateCategoryOptions.find(opt => opt.value === value)?.label || value}
                </Badge>
            )
        },
        {
            Header: "Description",
            accessor: "description"
        },
        {
            Header: "Status",
            accessor: "isActive",
            cell: ({ value }) => (
                <CustomStatus
                    statusText={value ? "Active" : "Inactive"}
                    color={value ? "#166534" : undefined}
                />
            )
        },
        {
            Header: "Usage Count",
            accessor: "usageCount"
        },
        {
            Header: "Last Used",
            accessor: "lastUsed"
        },
        {
            Header: "Created By",
            accessor: "createdBy"
        },
        ...(effectivePermission?.view || effectivePermission?.update || effectivePermission?.delete ? [actionHeader] : [])
    ];

    const [columns] = useState([...tableColumns])
    const [selectedColumns, setSelectedColumns] = useState([...tableColumns])

    const dataColumn = tableColumns.filter((item) => selectedColumns.some((column) => column.Header === item.Header))

    const handlePreview = async (id) => {
        try {
            const response = await offerLetterService.previewTemplate(id)
            setPreviewData(response)
            onPreviewOpen()
        } catch (error) {
            toast.error("Failed to preview template")
            console.error(error)
        }
    }

    return (
        <div>
            <CommonCheckTable
                title={title}
                showInlineTitle={true}
                isLoading={isLoading}
                columnData={columns}
                dataColumn={dataColumn}
                allData={data}
                tableData={data}
                searchDisplay={displaySearchData}
                setSearchDisplay={setDisplaySearchData}
                searchedDataOut={searchedData}
                setSearchedDataOut={setSearchedData}
                tableCustomFields={[]}
                access={effectivePermission}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                setDelete={setDeleteMany}
                onOpen={() => handleOpenModal()}
                // pagination/sort/search
                setCurrentPage={setCurrentPage}
                fetchData={fetchData}
                currentPage={currentPage}
                sortOrder={sortOrder}
                searchbox={searchbox}
                handleSortChange={handleSortChange}
                handleSearchChange={handleSearchChange}
                setSearchbox={setSearchbox}
                finalSearchedData={finalSearchedData}
                metadata={metadata}
                setFinalSearchedData={setFinalSearchedData}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalPages={metadata?.totalPages || 1}
                totalRecords={metadata?.totalRecords || 0}
                isPagination={true}
                AdvanceSearch={
                    <Button
                        variant="outline"
                        className="advance-search-button bg-transparent"
                        colorScheme="brand"
                        leftIcon={<SearchIcon />}
                        size="sm"
                    >
                        Advanced Search
                    </Button>
                }
            />

            {/* Create/Edit Template Modal */}
            <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
                <ModalOverlay />
                <ModalContent maxW="1000px" maxH="90vh">
                    <ModalHeader>
                        {editingTemplate ? 'Edit Template' : 'Create New Template'}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6} overflowY="auto">
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <FormLabel>
                                    Template Name <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter template name"
                                />
                            </Box>

                            <Box>
                                <FormLabel>Description</FormLabel>
                                <Textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief description of this template"
                                    rows={2}
                                />
                            </Box>

                            <Box>
                                <FormLabel>
                                    Category <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    {templateCategoryOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Select>
                            </Box>

                            <Box>
                                <FormLabel>
                                    Email Subject <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <Input
                                    value={formData.subject}
                                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                    placeholder="Email subject line"
                                />
                            </Box>

                            <Box>
                                <FormLabel>
                                    Template Content <span style={{ color: 'red' }}>*</span>
                                </FormLabel>
                                <OfferContentEditor
                                    value={formData.content}
                                    onChange={(value) => setFormData({ ...formData, content: value })}
                                    showPlaceholders={true}
                                />
                            </Box>

                            <HStack spacing={6}>
                                <Box>
                                    <FormLabel mb={0}>Active</FormLabel>
                                    <Switch
                                        isChecked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                </Box>

                                <Box>
                                    <FormLabel mb={0}>Set as Default</FormLabel>
                                    <Switch
                                        isChecked={formData.isDefault}
                                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                                    />
                                </Box>
                            </HStack>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="outline" mr={3} onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleSubmit}>
                            {editingTemplate ? 'Update' : 'Create'} Template
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Preview Modal */}
            <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
                <ModalOverlay />
                <ModalContent maxW="900px" maxH="90vh">
                    <ModalHeader>Template Preview</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6} overflowY="auto">
                        {previewData ? (
                            <VStack spacing={4} align="stretch">
                                <Box p={3} bg="gray.50" borderRadius="md">
                                    <Text fontWeight="semibold" mb={1}>{previewData.template?.name}</Text>
                                    <Badge colorScheme="purple">{previewData.template?.category}</Badge>
                                    <Text fontSize="sm" color="gray.600" mt={2}>
                                        {previewData.template?.description}
                                    </Text>
                                </Box>

                                <Box>
                                    <Text fontWeight="semibold" mb={2}>Subject:</Text>
                                    <Text bg="blue.50" p={3} borderRadius="md">{previewData.preview?.subject}</Text>
                                </Box>

                                <Box>
                                    <Text fontWeight="semibold" mb={2}>Content:</Text>
                                    <Box
                                        p={4}
                                        bg="white"
                                        border="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewData.preview?.content) }}
                                    />
                                </Box>
                            </VStack>
                        ) : null}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onPreviewClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <CommonDeleteModel
                isOpen={deleteMany}
                onClose={() => setDeleteMany(false)}
                type="Templates"
                handleDeleteData={handleDeleteTemplates}
                ids={selectedValues}
                onCancel={() => setSelectedValues([])}
            />
        </div>
    );
};

export default OfferTemplates;
